import type { NodeId } from './types.ts';

type HeapEntry = [NodeId, number];

class MinPriorityQueue {
  heap: Array<HeapEntry | null>;
  heapMap: Record<NodeId, number>;

  constructor() {
    this.heap = [null]; // array representation of binary heap
    this.heapMap = {};
  }

  offerWithPriority(node: NodeId, priority: number) {
    const index = this.heap.push([node, priority]) - 1;

    this.heapMap[node] = index;
    this.bubble(index);
  }

  raisePriority(node: NodeId, priority: number) {
    const index = this.heapMap[node];
    const entry = this.heap[index];
    if (!entry) {
      return;
    }
    entry[1] = priority;

    this.bubble(index);
  }

  poll() {
    if (this.heap.length === 1) {
      return null;
    }
    if (this.heap.length === 2) {
      const value = this.heap.pop()?.[0];
      if (value === undefined) {
        return null;
      }
      delete this.heapMap[value];
      return value;
    }

    const value = this.heap[1]?.[0];
    if (value === undefined) {
      return null;
    }
    delete this.heapMap[value];

    this.heap[1] = this.heap.pop() ?? null;
    if (this.heap[1]) {
      this.heapMap[this.heap[1][0]] = 1;
    }

    this.sink(1);

    return value;
  }

  isEmpty() {
    return this.heap.length === 1;
  }

  bubble(i: number) {
    while (i > 1) {
      const parentIndex = i >> 1; // floor(i/2)

      if (!this.isHigherPriority(i, parentIndex)) {
        break;
      }

      this.swap(i, parentIndex);
      i = parentIndex;
    }
  }

  sink(i: number) {
    while (i * 2 < this.heap.length) {
      const isRightChildHigherPriority =
        this.heap[i * 2 + 1] === undefined ? false : this.isHigherPriority(i * 2 + 1, i * 2); // i*2 +1 might be null
      const childIndex = isRightChildHigherPriority ? i * 2 + 1 : i * 2;

      if (this.isHigherPriority(i, childIndex)) {
        break;
      }

      this.swap(i, childIndex);
      i = childIndex;
    }
  }

  swap(i: number, j: number) {
    const first = this.heap[i];
    const second = this.heap[j];
    if (!first || !second) {
      return;
    }
    this.heapMap[first[0]] = j;
    this.heapMap[second[0]] = i;

    const temp = this.heap[i];
    this.heap[i] = this.heap[j];
    this.heap[j] = temp;
  }

  isHigherPriority(i: number, j: number) {
    const first = this.heap[i];
    const second = this.heap[j];
    return !!first && !!second && first[1] < second[1]; // lower score -> higher priority
  }
}

export default MinPriorityQueue;
